import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { environment } from 'projects/service/src/environments/environment';
import {
    DataManager,
    DataUtil,
    QueryList,
    Query,
    ParamOption,
    ODataAdaptor,
    QueryOptions
} from '@syncfusion/ej2-data';
import { Ajax } from '@syncfusion/ej2-base';
import { CommandColumnService, GridComponent, InfiniteScrollService, PageService, SelectionService, SortService, ToolbarService } from '@syncfusion/ej2-angular-grids';
import { CustomAdaptor, gridActionBeginForFiltering, gridActionsForFiltering, updateFilterIcon, applyInfiniteGridTotalCount, bindInfiniteScrollPrefetch } from '../../utils/grid-functions';
import { ClickEventArgs } from '@syncfusion/ej2-angular-navigations';
import { CommonService } from '../../services/common.service';
import { UserRoles } from '../../models/app.model';
import { CheckBoxComponent } from '@syncfusion/ej2-angular-buttons';
import { MasterItemcodePopupComponent } from 'projects/shared/src/lib/components/master-itemcode-popup/master-itemcode-popup.component';

const BASE_URL = environment.apiUrl;
@Component({
    selector: 'app-items-advanced-search',
    templateUrl: './items-advanced-search.component.html',
    styleUrls: ['./items-advanced-search.component.scss'],
    providers: [
        ToolbarService,
        SortService,
        PageService,
        InfiniteScrollService,
        SelectionService, CommandColumnService
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ItemsAdvancedSearchComponent implements OnInit {


    UserRoles: typeof UserRoles;
    USER: any;
    isExpanded: boolean = true;
    data: any;
    mainGrid: any;
    filterSettings: any;
    query: Query;
    selectedRowData: any;
    public pageSettings: Object
    public infiniteScrollSettings: any = { enableCache: false, initialBlocks: 3, maxBlocks: 10 };
    totalRecords: number = 0;

    @Input() warehouse: string;
    @Input() category: string = 'servieable';
    @Input() module: string;
    @Input() serviceItemGroup: string;
    @Input() technicianId: string;

    @Output() onClose = new EventEmitter();
    @ViewChild('columnChooser') columnChooser;
    @ViewChild('onHandCheckbox') onHandCheckbox: CheckBoxComponent;
    @ViewChild('itemsAdvancedSearchGrid') itemsAdvancedSearchGrid: GridComponent;
    gridHeight: number = window.innerHeight - 300;
    isOnHandChecked: boolean;

    constructor(
        private commonService: CommonService,
        private dialog: MatDialog
    ) {
        this.commonService.USER;
    }

    showMasterItemCodePopup(eqData: any) {
        if (!eqData?.itemCode && !eqData?.item) return;
        const dialogRef = this.dialog.open(MasterItemcodePopupComponent, {
            panelClass: 'master-itemcode-popup-dialog',
            width: '80%',
            height: '80%',
            data: eqData
        });
        dialogRef.afterClosed().subscribe((result) => { });
    }

    ngOnInit(): void {
        this.UserRoles = UserRoles;
        this.USER = this.commonService.USER;
        this.pageSettings = { pageSizes: [20, 50, 100, 500], pageSize: 50 };
        let url = `${BASE_URL}ServiceItem/GetServiceItemSearch?technicianId=${this.technicianId}`;

        if (this.module === 'ReturnOrder') {
            url = `${BASE_URL}ReturnOrder/GetServiceItemsForReturnOrder?warehouse=${this.warehouse}&category=${this.category}`;
        }
        this.filterSettings = { type: 'Menu' };
        this.data = new DataManager({
            url,
            adaptor: new CustomAdaptor()
        });
        this.query = new Query().addParams('company', this.commonService.USER.company);
        if (this.serviceItemGroup) {
            this.query.addParams('serviceItemGroup', this.serviceItemGroup);
        }
    }

    ngAfterViewInit(): void {
        this.setGrid();
    }

    setGrid() {
        const columns: any = [
            { field: 'company', headerText: 'Company', width: 120, visible: true, nonHideable: true, type: 'string', filter: { type: 'Excel' } },
            { field: 'item', headerText: 'Item', visible: true, type: 'string', filter: { type: 'Excel' } },
            { field: 'itemDescription', headerText: 'Item Description', visible: true, type: 'string' },
            { field: 'priceUnit', headerText: 'Price Unit', visible: false, type: 'string' },
            { field: 'serviceItemGroup', headerText: 'Service Item Group', visible: true, type: 'string', filter: { type: 'Excel' } },
            { field: 'status', headerText: 'Status', type: 'string', textAlign: 'Left', width: 120, filter: { type: 'Excel' } },
            { field: 'alternateItem', headerText: 'Alternate Item', type: 'string', format: 'n', textAlign: 'Left' },
            { field: 'costComponent', headerText: 'Cost Component', visible: true, type: 'string' },
            { field: 'itemGroup', headerText: 'Item Group', visible: true, type: 'string', filter: { type: 'Excel' } },
            {
                field: 'Actions', headerText: 'Actions', textAlign: 'Center', width: 120, allowFiltering: false, allowSorting: false,
                freeze: 'Right', allowReordering: false, commands: [
                    {
                        title: 'Proceed',
                        buttonOption: {
                            iconCss: `fas fa-arrow-right`,
                            cssClass: `e-flat action-button`
                        }
                    }
                ]
            }
        ]

        if (![UserRoles.isc, UserRoles.iscManager, UserRoles.IscSupervisor].includes(this.commonService.USER.role)) {
            const index = columns.findIndex(x => x.field === 'itemDescription');
            columns.splice(index, 0, { field: 'onHand', headerText: 'On Hand', type: 'number', format: 'n' });
        }

        this.mainGrid = {
            columns,
            toolbar: [
                { text: '', id: 'on-hand-checkbox', align: 'Right', template: this.onHandCheckbox, cssClass: 'me-4', tooltipText: 'On Hand' },
                'Search',
                { text: '', id: 'clear-filter', align: 'Right', prefixIcon: 'fas fa-filter', cssClass: `grid-filter-icon`, tooltipText: 'Clear all Filters' }

            ],
        };
    }

    toolbarClick(args: ClickEventArgs, grid: GridComponent) {
        if (args.item.id === 'clear-filter') {
            grid.clearFiltering();
            grid.search('');
            this.isOnHandChecked = false;
            this.onHandCheckbox.checked = false;
            this.itemsAdvancedSearchGrid.removeFilteredColsByField('onHand');
        }
    }

    onGridCreated() {
        updateFilterIcon({ requestType: 'filtering' }, this.itemsAdvancedSearchGrid);
        bindInfiniteScrollPrefetch(this.itemsAdvancedSearchGrid, () => this.totalRecords, 0, (count) => { this.totalRecords = count; });
    }

    onDataBound() {
        bindInfiniteScrollPrefetch(this.itemsAdvancedSearchGrid, () => this.totalRecords, 0, (count) => { this.totalRecords = count; });
    }

    getStatusClass(status) {
        return this.commonService.getStatusClass(status);
    }


    onGridActionBegin(args, grid?) {
        gridActionBeginForFiltering(args, grid);
        if (args.requestType === 'infiniteScroll') setTimeout(() => this.itemsAdvancedSearchGrid?.hideSpinner());
    }

    onGridActionComplete(args, grid) {
        if (args.requestType === 'infiniteScroll') setTimeout(() => grid?.hideSpinner());
        gridActionsForFiltering(args, grid);
        updateFilterIcon(args, grid);
        applyInfiniteGridTotalCount(grid, (count) => { this.totalRecords = count; }, args.requestType, args);
    }

    onCommandClick(args: any) {
        if (args.commandColumn.title === 'Proceed') {
            this.onClose.emit(args.rowData);
        }
    }

    close() {
        this.onClose.emit(null);
    }

    proceed() {
        this.onClose.emit(this.selectedRowData);
    }

    rowSelected(args) {
        this.selectedRowData = args.data;
    }

    onHoldCheckboxChange(e) {
        if (e?.checked) {
            this.itemsAdvancedSearchGrid.filterByColumn('onHand', 'doesnotcontain', 0);
        } else {
            this.itemsAdvancedSearchGrid.removeFilteredColsByField('onHand');
        }
        this.isOnHandChecked = e?.checked;
    }
}
